import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  GoogleAuthProvider, 
  onAuthStateChanged,
  User
} from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { createOrUpdateUser, getUserProfile } from '../services/userService';
import { doc, onSnapshot, updateDoc, serverTimestamp, Unsubscribe, getDoc } from 'firebase/firestore';
import { CircularProgress, Box, Typography } from '@mui/material';
import { UserProfile } from '../types/user';
import { initializePushNotifications, setupPushNotificationListeners } from '../services/pushNotificationService';

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  userRole: string | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  accountStatus: {
    type: 'suspended' | 'banned' | null;
    reason?: string;
    endDate?: Date;
  } | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

const AuthContext = createContext<AuthContextType | undefined>({
  currentUser: null,
  userProfile: null,
  userRole: null,
  loading: false,
  signInWithGoogle: async () => {},
  signOut: async () => {},
  accountStatus: null,
  setUserProfile: () => {},
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const LoadingScreen = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
    }}
  >
    <CircularProgress />
    <Typography variant="body1" sx={{ mt: 2 }}>
      Loading...
    </Typography>
  </Box>
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unsubscribeProfile, setUnsubscribeProfile] = useState<Unsubscribe | undefined>();
  const [accountStatus, setAccountStatus] = useState<AuthContextType['accountStatus']>(null);

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      setAccountStatus(null);
      console.log('Starting Google sign-in process...');
      
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      // First sign in with Google
      console.log('Attempting Google sign-in popup...');
      const result = await signInWithPopup(auth, provider);
      console.log('Google sign-in successful, checking user status...');
      
      // Check if user exists in our database
      const userRef = doc(db, 'users', result.user.uid);
      const userSnap = await getDoc(userRef);
      
      if (userSnap.exists()) {
        console.log('User found in database, checking status...');
        const userData = userSnap.data();
        
        // Check if user is banned
        if (userData.status === 'banned') {
          console.log('User is banned, handling ban...');
          await auth.signOut();
          setAccountStatus({
            type: 'banned',
            reason: userData.banReason || 'No reason provided'
          });
          throw new Error('ACCOUNT_STATUS_CHANGED');
        }

        // Check if user is suspended
        if (userData.status === 'suspended' && userData.suspendedUntil) {
          console.log('User is suspended, checking suspension period...');
          const suspensionEnd = userData.suspendedUntil.toDate();
          if (suspensionEnd > new Date()) {
            console.log('Suspension is still active, handling suspension...');
            await auth.signOut();
            setAccountStatus({
              type: 'suspended',
              endDate: suspensionEnd,
              reason: userData.suspensionReason || 'No reason provided'
            });
            throw new Error('ACCOUNT_STATUS_CHANGED');
          } else {
            console.log('Suspension has expired, reactivating account...');
            await updateDoc(userRef, {
              status: 'active',
              suspendedUntil: null,
              suspensionReason: null,
              updatedAt: serverTimestamp()
            });
            userData.status = 'active';
          }
        }
      } else {
        console.log('New user, will create profile...');
      }

      // Only create/update user if they're not banned/suspended
      console.log('Updating user profile...');
      await createOrUpdateUser(result.user);
      const profile = await getUserProfile(result.user.uid);
      
      if (!profile) {
        console.error('Failed to load user profile after creation/update');
        throw new Error('Failed to load user profile');
      }

      console.log('Sign-in process complete, setting user profile...');
      setUserProfile(profile);
      setUserRole(profile.role);
    } catch (error: any) {
      console.log('Error during sign-in:', error.message);
      
      // Handle Firebase cancellation errors gracefully
      if (error.code === 'auth/cancelled-popup-request' || 
          error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/popup-blocked') {
        console.log('User cancelled or closed the sign-in popup');
        // Don't throw error for user cancellations - just return silently
        return;
      }
      
      if (error.message !== 'ACCOUNT_STATUS_CHANGED') {
        // Only clear everything if it's not a status change
        setCurrentUser(null);
        setUserProfile(null);
        setUserRole(null);
        setAccountStatus(null);
        await auth.signOut();
        throw error;
      }
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      await auth.signOut();
      // Clear all user-related data
      setCurrentUser(null);
      setUserProfile(null);
      setUserRole(null);
      setAccountStatus(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      console.log('Auth state changed:', user ? 'User logged in' : 'No user');
      
      if (user && !accountStatus) {
        setCurrentUser(user);
        
        try {
          // Get initial profile data
          const userRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            const profile = userDoc.data() as UserProfile;
            
            // Check if user is banned or suspended
            if (profile.status === 'banned') {
              console.log('User is banned, signing out immediately');
              await auth.signOut();
              setAccountStatus({
                type: 'banned',
                reason: profile.banReason || 'No reason provided'
              });
              return;
            }
            
            if (profile.status === 'suspended' && profile.suspendedUntil) {
              const suspensionEnd = profile.suspendedUntil.toDate();
              if (suspensionEnd > new Date()) {
                console.log('User is suspended, signing out immediately');
                await auth.signOut();
                setAccountStatus({
                  type: 'suspended',
                  endDate: suspensionEnd,
                  reason: profile.suspensionReason || 'No reason provided'
                });
                return;
              }
            }
            
            setUserProfile(profile);
            setUserRole(profile.role);
          } else {
            // Create new user profile if it doesn't exist
            const newProfile = await createOrUpdateUser(user);
            if (newProfile) {
              setUserProfile(newProfile);
              setUserRole(newProfile.role);
            }
          }
          
          // Set up real-time listener for profile changes
          const unsubscribe = onSnapshot(userRef, (doc) => {
            if (doc.exists()) {
              const profile = doc.data() as UserProfile;
              
                             // Check if user status changed to banned or suspended
               if (profile.status === 'banned') {
                 console.log('Real-time: User is banned, signing out immediately');
                 auth.signOut().then(() => {
                   setAccountStatus({
                     type: 'banned',
                     reason: profile.banReason || 'No reason provided'
                   });
                 });
                 return;
               }
               
               if (profile.status === 'suspended' && profile.suspendedUntil) {
                 const suspensionEnd = profile.suspendedUntil.toDate();
                 if (suspensionEnd > new Date()) {
                   console.log('Real-time: User is suspended, signing out immediately');
                   auth.signOut().then(() => {
                     setAccountStatus({
                       type: 'suspended',
                       endDate: suspensionEnd,
                       reason: profile.suspensionReason || 'No reason provided'
                     });
                   });
                   return;
                 }
               }
              
              // Clear account status if user is now active
              if (profile.status === 'active' && accountStatus) {
                setAccountStatus(null);
              }
              
              setUserProfile(profile);
              setUserRole(profile.role);
            }
          });
          
          setUnsubscribeProfile(unsubscribe);

          // Check if push notifications were previously enabled
          const settingsDoc = await getDoc(doc(db, 'users', user.uid, 'settings', 'preferences'));
          const settings = settingsDoc.exists() ? settingsDoc.data() : null;
          
          if (settings?.notifications?.push) {
            // Only initialize if they were previously enabled
            const token = await initializePushNotifications(user.uid);
            if (token) {
              setupPushNotificationListeners();
            }
          }
        } catch (error) {
          console.error('Error setting up user profile:', error);
          // Handle error appropriately
        }
      } else {
        // User is signed out
        if (unsubscribeProfile) {
          unsubscribeProfile();
          setUnsubscribeProfile(undefined);
        }
        setCurrentUser(null);
        setUserProfile(null);
        setUserRole(null);
        // Don't clear accountStatus here - it should persist to show the status screen
      }
      
      setLoading(false);
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) {
        unsubscribeProfile();
        setUnsubscribeProfile(undefined);
      }
    };
  }, [unsubscribeProfile, accountStatus]);

  const value = {
    currentUser,
    userProfile,
    userRole,
    loading,
    signInWithGoogle,
    signOut,
    accountStatus,
    setUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 